import { PlusIcon } from '@acceso/design-system';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LOGO_FORM_DEFAULT_VALUES } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/constants';
import { TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchClipLogo } from '@/features/clips/api/patch-clips-logos';
import { usePostClipLogo } from '@/features/clips/api/post-clips-logos';
import { TPatchClipLogo, TPostClipLogo } from '@/features/clips/types';
import { usePostLogos } from '@/features/logos/api/post-logos';
import { TLogo, TLogosForm } from '@/features/logos/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { FormTypeEnum } from '@/types';
import { convertISOToSeconds } from '@/utils/hh-mm-ss-into-number';

export function useClipToolkitLogoFormState() {
  const {
    logoData,
    drawerRect,
    activeClip,
    formBox,
    playerVideoCurrentTime,
    getDateTimeCurrentClip,
    setLogoData,
    setFormBox,
    setActiveClip,
    setVisibleBox,
    drawnFormReset,
    setClipItems,
  } = useBroadcastScheduleProgram();
  const postClipLogo = usePostClipLogo();
  const patchClipLogo = usePatchClipLogo();
  const form = useForm<TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm>({
    mode: 'all',
    defaultValues: LOGO_FORM_DEFAULT_VALUES,
  });
  const drawerLogo = useDisclosureWithData();
  const { t } = useTranslation();
  const { mutateAsync: createLogos, isPending: isCreatingLogos } = usePostLogos();

  const renderAdditionalButtons = () => {
    return [
      {
        label: t('Crear nuevo logotipo'),
        icon: PlusIcon,
        onClick: () =>
          drawerLogo.open({
            form: null,
            type: FormTypeEnum.CREATE,
          }),
      },
    ];
  };

  const handleSubmitLogo = useCallback(
    async (data: TLogosForm): Promise<TLogo> => {
      try {
        const result = await createLogos(data);
        drawerLogo.close();
        return result;
      } catch (error) {
        console.error('Error during mutation', error);
        throw error;
      }
    },
    [createLogos]
  );

  const handleSubmit = async (data: TBroadcastScheduleProgramBodySegmentToolkitLogotiposForm) => {
    const start_datetime =
      data.start_datetime ?? getDateTimeCurrentClip(playerVideoCurrentTime) ?? null;
    const formatedData: TPostClipLogo = {
      ...data,
      start_datetime,
      end_datetime: data.end_datetime ?? start_datetime,
      clip_id: activeClip?.id || '',
      parameters: {
        ...data.parameters,
        bbox: data.parameters.bbox || [0.045, 0.075, 0.96, 0.925], // Full screen
      },
      entity_score: Number(data.entity_score) / 100,
    };
    if (logoData?.id) {
      handleUpdate(formatedData);
    } else {
      handleCreate(formatedData);
    }
  };

  const handleCreate = async (formatedData: TPostClipLogo) => {
    try {
      const item = await postClipLogo.mutateAsync(formatedData);
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_logos: [...(prev.clip_logos || []), item],
          };
        }
        return null;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === formatedData?.clip_id) {
            return { ...clip, clip_logos: [...(clip.clip_logos || []), item] };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al crear el logo:', error);
    }
  };

  const handleUpdate = async (formatedData: TPostClipLogo) => {
    const { clip_id, silently, ...restData } = formatedData;
    const xhrParams: TPatchClipLogo = {
      ...restData,
      silently,
      id: logoData?.id || formatedData.id || '',
    };
    try {
      const item = await patchClipLogo.mutateAsync(xhrParams);
      setActiveClip(prev => {
        if (prev) {
          return {
            ...prev,
            clip_logos: prev.clip_logos?.map(logo => {
              if (logo.id === item.id) {
                return item;
              }
              return logo;
            }),
          };
        }
        return prev;
      });
      setClipItems(prev =>
        prev.map(clip => {
          if (clip.id === clip_id) {
            return {
              ...clip,
              clip_logos: (clip.clip_logos || []).map(ocr => {
                if (ocr.id === item.id) {
                  return item;
                }
                return ocr;
              }),
            };
          }
          return clip;
        })
      );
      drawnFormReset();
    } catch (error) {
      console.error('Error al actualizar el logo:', error);
    }
  };

  const handleClickExit = useCallback(() => {
    form.reset(LOGO_FORM_DEFAULT_VALUES);

    // TODO: Here it avoids clearing all previous data, but when switching
    //  the active window, the group of the selected logo does not unfold.
    setFormBox(null);
  }, [setFormBox, setLogoData]);

  useEffect(() => {
    if (drawerRect && formBox === 'logo') {
      form.setValue('parameters.bbox', drawerRect);
    }
  }, [drawerRect]);

  useEffect(() => {
    if (formBox !== 'logo') return;

    if (logoData?.id) {
      form.setValue('logo_id', logoData?.logo_id || '');
      form.setValue('parameters.bbox', logoData.parameters.bbox);
      form.setValue('start_datetime', new Date(logoData.start_datetime));
      form.setValue('end_datetime', new Date(logoData.end_datetime || logoData.start_datetime));
      form.setValue('entity_score', logoData.entity_score * 100);
      form.setValue('review_state', logoData.review_state);
      form.setValue('parameters.visibility', logoData.parameters.visibility);
      form.setValue('parameters.support_id', logoData.parameters.support_id);

      if (!activeClip || !logoData.start_datetime) return;

      const seconds = convertISOToSeconds(
        logoData.start_datetime,
        new Date(activeClip.start_datetime)
      );

      setVisibleBox({
        bbox: logoData?.parameters.bbox || [0, 0, 0, 0],
        frame: seconds,
        entity_label: logoData?.entity_label || '',
      });
    }
  }, [logoData]);

  return {
    form,
    drawerLogo,
    isCreatingLogos,
    handleUpdate,
    renderAdditionalButtons,
    handleSubmitLogo,
    handleSubmit,
    handleClickExit,
  };
}
